//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { CollectionSortType } from '@apis/collections/CollectionSortType';
import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { ConnectVendorType } from '@apis/collections/ConnectVendorType';
import { UniversalItemType } from '@models/UniversalItemType';
import { IStream } from '@streams-drawer/model/IStream';

import {
  InstagramWebLinks,
  PinterestWebLinks,
} from 'app/constants/social-links';

export enum StreamsDrawerContentViews {
  GRID = 'grid',
  DETAILS = 'details',
}

export enum StreamsDrawerGetItemsContext {
  FILTERS_CHANGE = 'filters_change',
  FIRST_PAGE = 'first_page',
  NEXT_PAGE = 'next_page',
}

export enum StreamsDrawerTrackingStates {
  NOT_TRACKING = 'not_tracking',
  TRACKING = 'tracking',
  TRACKING_COMPLETE = 'tracking_complete',
}

// 2 minutes in ms
export const MAX_TRACKING_TIMEOUT = 120000;

// Beginning substring of semantic tag stream name.
// Stream name is set as part of content discovery onboarding.
export const SEMANTIC_STREAM_NAME = 'Recent Images of';

export enum StreamModalType {
  STANDARD = 'standard',
  ASSET_FEEDS = 'assetFeeds',
  ASSET_FEEDS_CLOSE_CONFIRMATION = 'assetFeedsCloseConfirmation',
}

export enum StreamsLaunchContext {
  MODERATION_SERVICES = 'moderation',
  CONNECT = 'connect',
  GALLERIES = 'galleries',
  ASSET_FEEDS = 'assetFeeds',
}

export enum StreamsDrawerStreamType {
  RECOMMENDED = 'recommended',
  POPULAR = 'popular',
  INSPIRED = 'inspired',
  SEMANTIC = 'semantic',
  STANDARD = 'standard',
  SUMMARY = 'summary',
  ASSET_FEEDS = 'assetFeeds',
  GALLERY_UPLOADS = 'galleryUploads',
  TAGGED_BY_CURALTE = 'taggedByCuralate',
  SIMILARITY_TO_APPROVED = 'similarityToApproved',
  CONNECT = 'connect',
}

export const DEFAULT_SORT_STATE = CollectionSortType.TIME;

export const DEFAULT_MODERATION_STATUS_STATE = CollectionStatusType.Unmoderated;

export const DEFAULT_CONNECT_VENDOR_STATE = ConnectVendorType.All;

export const CONNECT_CUSTOM_THUMBNAIL_MAX_WIDTH = 400;

export const INSPIRED_STREAM_NAME_PATTERN = /^inspired by your instagram$/i;
export const POPULAR_STREAM_NAME_PATTERN = /^popular with your audience$/i;
export const SEMANTIC_STREAM_NAME_PATTERN = /^recent images of/i;
export const GALLERY_UPLOADS_STREAM_NAME_PATTERN = /^Gallery Uploads$/i;
export const GOOGLE_ASSET_FEED_STREAM_NAME_PATTERN =
  /^Google Asset Feed Stream$/i;
export const TAGGED_BY_CURALATE_STREAM_NAME_PATTERN = /^tagged by curalate$/i;
export const CONNECT_STREAM_NAME_PATTERN = /^connect$/i;
export const SIMILARITY_TO_APPROVED_STREAM_NAME_PATTERN =
  /^Similar To Previously Approved$/i;

export function nameToStreamType(
  stream: Pick<IStream, 'name'>,
): StreamsDrawerStreamType {
  let streamType: StreamsDrawerStreamType;

  if (INSPIRED_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.INSPIRED;
  } else if (POPULAR_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.POPULAR;
  } else if (SEMANTIC_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.SEMANTIC;
  } else if (GALLERY_UPLOADS_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.GALLERY_UPLOADS;
  } else if (GOOGLE_ASSET_FEED_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.ASSET_FEEDS;
  } else if (TAGGED_BY_CURALATE_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.TAGGED_BY_CURALTE;
  } else if (CONNECT_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.CONNECT;
  } else if (SIMILARITY_TO_APPROVED_STREAM_NAME_PATTERN.exec(stream.name)) {
    streamType = StreamsDrawerStreamType.SIMILARITY_TO_APPROVED;
  } else {
    streamType = StreamsDrawerStreamType.STANDARD;
  }

  return streamType;
}

// If this is the connect stream, use the name Visual Syndication Network.
// Otherwise, use the name on the stream config
export const getRecommendedStreamName = (stream: Pick<IStream, 'name'>) => {
  const streamType = nameToStreamType(stream);
  let streamName = stream.name;

  if (streamType === StreamsDrawerStreamType.CONNECT) {
    streamName = 'Visual Syndication Network';
  } else if (streamType === StreamsDrawerStreamType.TAGGED_BY_CURALTE) {
    streamName = 'Tagged By Bazaarvoice';
  }

  return streamName;
};

export function getUserHref(username: string, network: string) {
  if (username) {
    if (network.toLowerCase() === UniversalItemType.Pinterest) {
      return PinterestWebLinks.getUserLink(username);
    } else {
      return InstagramWebLinks.getUserLink(username);
    }
  } else {
    return '';
  }
}

export const DEFAULT_DATE_RANGE_STRING = 'recently';

export const STREAM_CONTAINER_ID_QUERY_PARAM = 'openStreamId';

export const StreamsDrawerTrackingIds = {
  CREATE_STREAM_MOVED_CONTENT_LIBRARY_LINK:
    'streams-drawer-create-stream-moved-content-library-link',
  MANUAL_MODERATION_REJECT_ITEM: 'streams-drawer-manual-moderation-reject-item',
  MANUAL_MODERATION_RETRY_REJECTION_REASONS:
    'streams-drawer-manual-moderation-retry-rejection-reasons',
  NEW_CONTENT_STREAM_BUTTON: 'streams-drawer-new-content-stream-button',
  STREAMS_DRAWER_PAGE_HEADER_NAME: 'streams-drawer-page-header-name',
};
